import { FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Stack, ThemeProvider, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { SIGNUP_MINIMUM_AGE } from '@mopla/constants';
import { passengerThemeV2 } from '@mopla/mui';
import { guardPhoneOnChange } from '@mopla/utils';

import { MplInputV2 } from '../../atoms/MplInputV2/MplInputV2';
import { Switch } from '../../atoms/Switch/Switch';
import { DateOfBirthFormInput } from '../../molecules/DateOfBirthInput/DateOfBirthFormInput';
import {
	isAboveMaximumAge,
	isBelowMinimumAge,
} from '../../molecules/DateOfBirthInput/utils';
import {
	EMlpIconTypes,
	EMplVariantTypes,
	MplIconMessage,
} from '../../molecules/MplIconMessage';

import { NameChangeNote } from './NameChangeNote';
import { InputsWrapper } from './PersonalDetailsForm.styles';

export enum EPersonalDetailsVariant {
	Registration = 'REGISTRATION',
	Profile = 'PROFILE',
}

interface IFormFieldsV2Props {
	showAddressToggle?: boolean;
	showCompanyField?: boolean;
	showDates?: boolean;
	withPaddings?: boolean;
	/**
	 * Where the user's own details are edited; adds the name-change and data-sharing notes.
	 * `Profile` also locks the name fields. Omit for plain fields (e.g. an invoice recipient).
	 */
	variant?: EPersonalDetailsVariant;
}

export const FormFieldsV2: FC<IFormFieldsV2Props> = ({
	showDates = true,
	showAddressToggle = true,
	showCompanyField = false,
	withPaddings = true,
	variant,
}) => {
	const isNameReadOnly = variant === EPersonalDetailsVariant.Profile;
	const { t } = useTranslation(['personalDetails', 'validation']);
	const {
		formState: { errors, touchedFields },
		getValues,
		setError,
		clearErrors,
	} = useFormContext();

	const [saveAddress, setSaveAddress] = useState<boolean>(false);

	const checkDateOfBirth = () => {
		const { day, month, year } = getValues();

		const allTouched =
			touchedFields?.day && touchedFields?.month && touchedFields?.year;
		const allEmpty = !day && !month && !year;

		if (allTouched && allEmpty) {
			return setError('dateOfBirth', {
				message: t('validation:dateOfBirth.required'),
			});
		}

		const date = dayjs(
			`${year}-${month}-${day}`,
			['YYYY-MM-DD', 'YYYY-M-D', 'YYYY-MM-D', 'YYYY-M-DD'],
			true
		);
		const isDateValid = date.isValid();

		if (day && month && year && !isDateValid) {
			return setError('dateOfBirth', {
				message: t('validation:dateOfBirth.date_invalid'),
			});
		}

		// The future/age checks only make sense for a fully valid date. Running
		// them on an incomplete or invalid date (e.g. month > 12 before every
		// field is filled) made `isBelowMinimumAge` return a false positive, so
		// the "age" error was shown for what is really an invalid date. (MC-11020)
		if (isDateValid) {
			const isFutureDate = date.isAfter(dayjs());

			if (isFutureDate) {
				return setError('dateOfBirth', {
					message: t('validation:dateOfBirth.date_invalid'),
				});
			}

			if (isBelowMinimumAge(date)) {
				return setError('dateOfBirth', {
					message: t('validation:dateOfBirth.age_incorrect', {
						minAge: SIGNUP_MINIMUM_AGE,
					}),
				});
			}

			if (isAboveMaximumAge(date)) {
				return setError('dateOfBirth', {
					message: t('validation:dateOfBirth.date_invalid'),
				});
			}
		}

		clearErrors('dateOfBirth');
	};

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<Stack gap={4} p={withPaddings ? '0 24px' : 0}>
				<Stack gap={2}>
					<Controller
						name="firstName"
						render={({ field }) => (
							<MplInputV2
								{...field}
								variant="outlined"
								label={`${t('label.firstName')} *`}
								helperText={
									errors.firstName?.message &&
									t(`validation:${errors.firstName?.message}`)
								}
								error={Boolean(errors.firstName?.message)}
								inputProps={{
									'data-testid': 'personal-detailsForm-name-textField',
								}}
								disabled={isNameReadOnly}
								clearable
							/>
						)}
					/>
					<Controller
						name="surname"
						render={({ field }) => (
							<MplInputV2
								{...field}
								variant="outlined"
								label={`${t('label.surname')} *`}
								helperText={
									errors.surname?.message &&
									t(`validation:${errors.surname?.message}`)
								}
								error={Boolean(errors.surname?.message)}
								inputProps={{
									'data-testid': 'personal-detailsForm-surname-textField',
								}}
								disabled={isNameReadOnly}
								clearable
							/>
						)}
					/>
				</Stack>
				{variant &&
					(isNameReadOnly ? (
						<NameChangeNote />
					) : (
						<MplIconMessage
							dataTestid="personal-detailsFormV2-nameChange-note"
							type={EMlpIconTypes.Warning}
							variant={EMplVariantTypes.Warning}
							textVariant="b2"
							message={t('text.name_change_irreversible')}
						/>
					))}
				{showDates && (
					<DateOfBirthFormInput
						dataTestIdPrefix="personal-detailsForm"
						onValidateDate={checkDateOfBirth}
					/>
				)}
				{variant && (
					<Typography variant="l1" color="solutions.600" textAlign="center">
						{t('text.share_info_with_tc_note')}
					</Typography>
				)}
				{showCompanyField ? (
					<Controller
						name="company"
						render={({ field }) => (
							<MplInputV2
								{...field}
								variant="outlined"
								label={`${t('label.company')}`}
								inputProps={{
									'data-testid': 'personal-detailsForm-company-textField',
								}}
								helperText={
									errors.company?.message &&
									t(`validation:${errors.company?.message}`)
								}
								error={Boolean(errors.company?.message)}
								clearable
							/>
						)}
					/>
				) : (
					<Controller
						name="phoneNumber"
						render={({ field }) => (
							<MplInputV2
								{...field}
								onChange={guardPhoneOnChange(field.onChange)}
								type="tel"
								variant="outlined"
								label={`${t('label.phoneNumber')} *`}
								helperText={
									errors.phoneNumber?.message &&
									t(`validation:${errors.phoneNumber?.message}`)
								}
								error={Boolean(errors.phoneNumber?.message)}
								inputProps={{
									'data-testid': 'personal-detailsForm-phone-textField',
								}}
								clearable
							/>
						)}
					/>
				)}
				<Stack gap={2}>
					<InputsWrapper firstWidth="70%" secondWidth="30%">
						<Controller
							name="street"
							render={({ field }) => (
								<MplInputV2
									{...field}
									variant="outlined"
									label={`${t('label.street')} *`}
									inputProps={{
										'data-testid': 'personal-detailsForm-street-textField',
									}}
									helperText={
										errors.street?.message &&
										t(`validation:${errors.street?.message}`)
									}
									error={Boolean(errors.street?.message)}
									clearable
								/>
							)}
						/>
						<Controller
							name="streetNumber"
							render={({ field }) => (
								<MplInputV2
									{...field}
									variant="outlined"
									label={`${t('label.number')} *`}
									inputProps={{
										'data-testid':
											'personal-detailsForm-streetNumber-textField',
									}}
									helperText={
										errors.streetNumber?.message &&
										t(`validation:${errors.streetNumber?.message}`)
									}
									error={Boolean(errors.streetNumber?.message)}
									clearable
								/>
							)}
						/>
					</InputsWrapper>
					<InputsWrapper firstWidth="30%" secondWidth="70%">
						<Controller
							name="postCode"
							render={({ field }) => (
								<MplInputV2
									{...field}
									variant="outlined"
									label={`${t('label.postCode')} *`}
									helperText={
										errors.postCode?.message &&
										t(`validation:${errors.postCode?.message}`)
									}
									error={Boolean(errors.postCode?.message)}
									inputProps={{
										'data-testid': 'personal-detailsForm-postCode-textField',
									}}
									clearable
								/>
							)}
						/>
						<Controller
							name="location"
							render={({ field }) => (
								<MplInputV2
									{...field}
									variant="outlined"
									label={`${t('label.location')} *`}
									inputProps={{
										'data-testid': 'personal-detailsForm-location-textField',
									}}
									helperText={
										errors.location?.message &&
										t(`validation:${errors.location?.message}`)
									}
									error={Boolean(errors.location?.message)}
									clearable
								/>
							)}
						/>
					</InputsWrapper>
					{showAddressToggle && (
						<Stack
							justifyContent="space-between"
							alignItems="center"
							flexDirection="row"
							gap={2}
							component="label"
						>
							<Typography variant="b2" color="primary">
								{t('text.save_as_home_address')}
							</Typography>
							<Switch
								checked={saveAddress}
								onChange={() => setSaveAddress((prev) => !prev)}
								data-testid="personal-detailsForm-saveAddress-switch"
							/>
						</Stack>
					)}
				</Stack>
			</Stack>
		</ThemeProvider>
	);
};
